import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Sparkles, MapPin, CalendarDays, Clock, Users, Copy, Mail, MessageSquare,
  Share2, ShieldCheck, ChevronDown, ChevronUp, Phone,
} from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';
import { useAuth } from '@/contexts/AuthContext';
import AuthGateModal from '@/components/AuthGateModal';
import TestimonialsCarousel from '@/components/TestimonialsCarousel';
import PastEventSummaryCard from '@/components/PastEventSummaryCard';
import { formatEventCountdown } from '@/utils/eventCountdown';

// Guard against non-IANA timezone strings written to the DB (e.g. free-text
// like "USA (Central Time)"). An invalid timeZone option to Intl throws
// RangeError which would blank the whole page. Returns undefined on any
// invalid input so the caller falls back to the browser's local zone.
const safeEventTz = (raw) => {
  if (!raw || typeof raw !== 'string') return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: raw }).format(new Date());
    return raw;
  } catch {
    return undefined;
  }
};

/**
 * Public event detail page - fetches /events/slug/:slug and renders content
 * blocks in order. Also handles SEO tags (document.title + meta description).
 *
 * Draft events return 404; caller is expected to route accordingly.
 */
const EventDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Bundle 155 - countdown state now carries the semantic
  // marker (future / present / past) so JSX can render the
  // right copy without duplicating date math. See
  // frontend/src/utils/eventCountdown.js for the formatter.
  const [countdown, setCountdown] = useState({ state: 'future', label: '' });
  const [copied, setCopied] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const { trackEvent } = usePixels();

  // Bundle 51 - `?rsvp=1` in the URL means the user just returned from
  // a Google / magic-link redirect. If they are signed in, drop them
  // straight into the RSVP flow. Otherwise reopen the modal.
  useEffect(() => {
    if (searchParams.get('rsvp') !== '1') return;
    if (user?.id) {
      navigate(`/events/${slug}/rsvp`, { replace: true });
    } else {
      setAuthOpen(true);
    }
  }, [searchParams, user, slug, navigate]);

  const handleRsvpClick = () => {
    if (user?.id) {
      navigate(`/events/${slug}/rsvp`);
    } else {
      setAuthOpen(true);
    }
  };

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/events/slug/${slug}`);
        setPayload(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // SEO - Emergent's platform prerenders scraper-UA requests server-side
  // (see the `x-rendered-by: crawler-cache` header on prod). That prerender
  // captures whatever the SPA has written into <head> after mount, so any
  // meta tag we set here bubbles up to Facebook/Twitter/WhatsApp/LinkedIn.
  //
  // The tags we override per-event (in addition to base tags from
  // index.html): title, description, og:title/description/image/url/type,
  // twitter:title/description/image/url, and the <link rel="canonical">.
  useEffect(() => {
    if (!payload?.event) return;
    const e = payload.event;
    const eventUrl = typeof window !== 'undefined' ? window.location.href.split('#')[0] : '';
    const img = e.seo_og_image_url || e.hero_image_detail_url || e.hero_image_url;
    const shareTitle = e.seo_social_share_title || e.title;
    const shareDesc = e.seo_social_share_description || e.summary || '';

    document.title = e.seo_page_title || `${e.title} - Dostination`;
    setMeta('description', e.seo_meta_description || e.summary || '');

    setMeta('og:type', 'event', true);
    setMeta('og:title', shareTitle, true);
    setMeta('og:description', shareDesc, true);
    setMeta('og:url', eventUrl, true);
    if (img) {
      setMeta('og:image', img, true);
      setMeta('og:image:secure_url', img, true);
      setMeta('og:image:alt', shareTitle, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', shareTitle);
    setMeta('twitter:description', shareDesc);
    setMeta('twitter:url', eventUrl);
    if (img) {
      setMeta('twitter:image', img);
      setMeta('twitter:image:alt', shareTitle);
    }

    setCanonical(eventUrl);
  }, [payload]);

  // ViewContent - fires once per event view. Fires only after the event
  // payload has loaded so we can include title, id, price, and currency.
  useEffect(() => {
    if (!payload?.event) return;
    const e = payload.event;
    const tier = payload.active_pricing_tier;
    const price = tier?.price_cents ? tier.price_cents / 100 : null;
    const p = {
      content_name: e.title,
      content_ids: [e.slug || e.id],
      content_category: 'event',
    };
    if (price != null && price > 0) {
      p.value = price;
      p.currency = tier?.currency || 'USD';
    }
    trackEvent('ViewContent', p);
  }, [payload, trackEvent]);

  // Countdown - Bundle 155 uses the shared formatter that
  // handles past events explicitly (no more "Starts in Live"
  // regression on ended events).
  const startDate = payload?.event?.start_datetime || payload?.event?.start_date;
  const endDate = payload?.event?.end_datetime || payload?.event?.end_date;
  useEffect(() => {
    if (!startDate && !endDate) return;
    const tick = () => setCountdown(
      formatEventCountdown(startDate, { endDate }));
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [startDate, endDate]);

  const shareUrl = useMemo(() => (typeof window !== 'undefined' ? window.location.href : ''), [payload]);
  const shareTitle = payload?.event?.seo_social_share_title || payload?.event?.title || 'Dostination event';

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" />
      </div>
    );
  }

  if (notFound || !payload) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-24 text-center">
        <Sparkles className="h-8 w-8 text-secondary-500 mx-auto mb-4" />
        <h1 className="font-display text-3xl font-bold text-primary-900 mb-2">Not available yet</h1>
        <p className="text-neutral-600 mb-6">This event isn&apos;t published, or the link may be incorrect.</p>
        <Button asChild><Link to="/events">Browse events</Link></Button>
      </div>
    );
  }

  const { event, content_blocks, active_pricing_tier, capacity_indicator } = payload;
  const isPast = !!payload.is_past_event;
  const pastSummary = payload.past_event_summary || null;

  // A1: render date/time in the EVENT's timezone (not the browser's local zone),
  // so a Dallas event always says "August 15, 2026 - 7:00 PM CDT" regardless
  // of where the reader is sitting.
  //
  // Prod-safety patch: an event whose `timezone` field is a free-text string
  // (e.g. "USA (Central Time)") crashed Intl and blanked the page. Sanitise
  // once (cheap, no hook needed - keeps it above the Rules of Hooks trap
  // since this file has early returns above).
  const eventTz = safeEventTz(payload?.event?.timezone);
  const endDateIso = payload?.event?.end_datetime || payload?.event?.end_date;

  // Bug Y2: when the event spans multiple local days in its own timezone,
  // render as "Saturday, August 15 - Sunday, August 16, 2026" so the end
  // date isn't silently truncated. Same-day events keep the single-date
  // format ("Saturday, August 15, 2026").
  const _tzOpt = eventTz ? { timeZone: eventTz } : {};
  const _fmtDatePart = (iso, opts) =>
    new Date(iso).toLocaleDateString('en-US', { ...opts, ..._tzOpt });
  const _sameLocalDay = (a, b) => {
    if (!a || !b) return true;
    try {
      const dayFmt = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit', ..._tzOpt,
      });
      return dayFmt.format(new Date(a)) === dayFmt.format(new Date(b));
    } catch { return true; }
  };
  const dateLabel = startDate
    ? (endDateIso && !_sameLocalDay(startDate, endDateIso)
        ? `${_fmtDatePart(startDate, { weekday: 'long', month: 'long', day: 'numeric' })} - ${_fmtDatePart(endDateIso, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`
        : _fmtDatePart(startDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }))
    : 'Date to be announced';

  const _hasClockTime = (iso) => typeof iso === 'string' && /T\d{2}:\d{2}/.test(iso);
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', ..._tzOpt,
  });
  const timeLabel = _hasClockTime(startDate)
    ? (endDateIso && _hasClockTime(endDateIso)
        ? `${fmtTime(startDate)} - ${fmtTime(endDateIso)}`
        : fmtTime(startDate))
    : null;
  const tzShort = (() => {
    if (!eventTz || !startDate) return null;
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: eventTz, timeZoneName: 'short' })
        .formatToParts(new Date(startDate));
      const p = parts.find((x) => x.type === 'timeZoneName');
      return p?.value || null;
    } catch { return null; }
  })();

  return (
    <div>
      {/* HERO - matches the 21:9 detail-crop the admin sees in HeroCropModal */}
      <section className="relative overflow-hidden">
        <div className="w-full aspect-[21/9] min-h-[320px] bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 relative">
          {(event.hero_image_detail_url || event.hero_image_url) && (
            <img
              src={event.hero_image_detail_url || event.hero_image_url}
              alt={event.title}
              className="absolute inset-0 w-full h-full object-cover object-center"
              data-testid="hero-image"
            />
          )}
          {/* Readability scrim confined to the bottom ~40% so the image keeps
              its original color fidelity in the top 60%. Sits ABOVE the img. */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 40%, transparent 60%)',
            }}
          />
          {/* Signature gold accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary-500 to-transparent" />

          <div className="relative container mx-auto px-4 md:px-8 h-full flex flex-col justify-end pb-14">
            <span className="inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full bg-white/10 border border-white/20 text-secondary-200 text-xs font-semibold uppercase tracking-widest mb-4">
              <Sparkles className="h-3 w-3" /> {event.event_type?.replace(/_/g, ' ') || 'Event'}
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-3 max-w-3xl leading-tight">
              {event.title}
            </h1>
            {event.tagline && (
              <p className="text-lg md:text-xl text-primary-100 max-w-2xl leading-relaxed mb-5">{event.tagline}</p>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-primary-100">
              <span className="flex items-center gap-1.5" data-testid="hero-date-label"><CalendarDays className="h-4 w-4 text-secondary-300" />{dateLabel}</span>
              {timeLabel && (
                <span className="flex items-center gap-1.5" data-testid="hero-time-label"><Clock className="h-4 w-4 text-secondary-300" />{timeLabel}{tzShort ? ` ${tzShort}` : ''}</span>
              )}
              {(event.venue_name || event.city) && (
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-secondary-300" />
                  {[event.venue_name, event.city, event.state_region || event.state].filter(Boolean).join(' · ')}
                </span>
              )}
              {countdown.label && (
                <span
                  className="flex items-center gap-1.5"
                  data-testid={`hero-countdown-${countdown.state}`}
                >
                  <Clock className="h-4 w-4 text-secondary-300" />
                  {countdown.label}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="container mx-auto px-4 md:px-8 py-12 grid gap-8 lg:grid-cols-[1fr_360px]">
        {/* LEFT - content blocks */}
        <div className="space-y-6">
          {(content_blocks || []).map((b) => (
            <ContentBlockRenderer key={b.id} block={b} event={event} />
          ))}
        </div>

        {/* RIGHT - sticky RSVP card */}
        <aside className="lg:sticky lg:top-24 self-start space-y-4">
          {isPast && pastSummary && (
            <PastEventSummaryCard summary={pastSummary} />
          )}
          <Card className="border shadow-soft">
            <CardContent className="p-6">
              {isPast ? (
                <div className="mb-4">
                  <div className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-1">Reservation</div>
                  <div className="font-display text-2xl font-bold text-primary-900">This event has ended</div>
                  <div className="text-xs text-neutral-500 mt-1">If you attended, your ticket and post-event survey link remain accessible from your account.</div>
                </div>
              ) : active_pricing_tier ? (
                <div className="mb-4">
                  <div className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-1">{active_pricing_tier.name}</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-3xl font-bold text-primary-900">
                      ${(active_pricing_tier.price_cents / 100).toFixed(2)}
                    </span>
                    <span className="text-sm text-neutral-500">{active_pricing_tier.currency || 'USD'}</span>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <div className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-1">Reservation</div>
                  <div className="font-display text-2xl font-bold text-primary-900">RSVP by invitation</div>
                  <div className="text-xs text-neutral-500 mt-1">Payment details are shared by email after your RSVP is confirmed.</div>
                </div>
              )}

              {!isPast && capacity_indicator && (
                <div className="flex items-center gap-1.5 text-sm text-accent-700 mb-4">
                  <Users className="h-4 w-4" /> {capacity_indicator}
                </div>
              )}

              <Button
                size="lg"
                onClick={() => handleRsvpClick()}
                disabled={isPast}
                className="w-full bg-gradient-to-r from-secondary-500 to-secondary-600 text-primary-900 font-semibold shadow-gold hover:from-secondary-600 hover:to-secondary-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:from-neutral-200 disabled:to-neutral-300 disabled:text-neutral-500"
                data-testid="event-rsvp-button"
              >
                {isPast ? 'This event has ended' : 'Reserve your spot'}
              </Button>

              <div className="mt-5 pt-5 border-t border-neutral-100">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Share</p>
                <div className="grid grid-cols-4 gap-2">
                  <ShareButton
                    icon={MessageSquare}
                    label="WhatsApp"
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} - ${shareUrl}`)}`}
                  />
                  <ShareButton
                    icon={Phone}
                    label="SMS"
                    href={`sms:?&body=${encodeURIComponent(`${shareTitle} - ${shareUrl}`)}`}
                  />
                  <ShareButton
                    icon={Mail}
                    label="Email"
                    href={`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}`}
                  />
                  <button
                    onClick={doCopy}
                    className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 py-2 hover:border-secondary-400 hover:shadow-sm transition-all"
                  >
                    <Copy className="h-4 w-4 text-primary-700" />
                    <span className="text-[10px] text-neutral-600 font-medium">{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-2 text-xs text-neutral-500 px-1">
            <ShieldCheck className="h-4 w-4 text-success-600" />
            Sign in required. Your ticket is issued after a photo and one social.
          </div>
        </aside>
      </section>
      {/* Bundle 80.2 - per-event testimonials block. Auto-hides when
          the event has zero publishable testimonials. */}
      {event?.id ? (
        <div className="max-w-6xl mx-auto">
          <TestimonialsCarousel
            endpoint="/testimonials"
            params={{ event_id: event.id, limit: 6 }}
            fallbackEndpoint="/testimonials/featured"
            fallbackParams={{ limit: 3 }}
            eyebrow="What guests said"
            headline={`Reflections from past ${event?.format || 'Dostination'} guests.`}
            ctaHref="/testimonials"
            ctaLabel="See all"
          />
        </div>
      ) : null}
      {/* Bundle 79 Task C - subtle missed-connection discoverability, only when the event is completed. */}
      {(() => {
        try {
          const endIso = event?.end_datetime || event?.end_date;
          if (!endIso) return null;
          if (new Date(endIso) > new Date()) return null;
          if (!event?.slug) return null;
          return (
            <div className="max-w-6xl mx-auto px-4 md:px-8 mt-8 mb-10 text-center">
              <p className="text-xs text-neutral-500" data-testid="event-detail-missed-connection-cta">
                Missed someone at this event? <a
                  href={`/introductions/request/${event.slug}`}
                  className="text-secondary-800 underline hover:text-secondary-900"
                  data-testid="event-detail-missed-connection-link"
                >Send a quiet note back through us.</a>
              </p>
            </div>
          );
        } catch (_e) { return null; }
      })()}
      <AuthGateModal
        open={authOpen}
        onClose={(signedIn) => {
          setAuthOpen(false);
          if (signedIn && event?.slug) {
            navigate(`/events/${event.slug}/rsvp`);
          }
        }}
        rsvpRedirectPath={event?.slug ? `/events/${event.slug}` : '/'}
      />
    </div>
  );
};

const ShareButton = ({ icon: Icon, label, href }) => (
  <a
    href={href}
    target="_blank" rel="noopener noreferrer"
    className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 py-2 hover:border-secondary-400 hover:shadow-sm transition-all"
  >
    <Icon className="h-4 w-4 text-primary-700" />
    <span className="text-[10px] text-neutral-600 font-medium">{label}</span>
  </a>
);

/* ------------------------------------------------------------------------ */
/*  Content block renderers                                                  */
/* ------------------------------------------------------------------------ */

const BlockShell = ({ title, children }) => (
  <Card className="border shadow-soft">
    <CardContent className="p-6 md:p-7">
      {title && (
        <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-900 mb-4">{title}</h2>
      )}
      {children}
    </CardContent>
  </Card>
);

const isEmpty = (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0);

const ContentBlockRenderer = ({ block, event }) => {
  const c = block.content || {};

  switch (block.block_type) {
    case 'hero':
      // hero is rendered at top; if it has extra content beyond headline, show a kicker card
      if (isEmpty(c.kicker) && isEmpty(c.subheadline)) return null;
      return null;

    case 'overview':
      if (isEmpty(c.body) && isEmpty(c.body_html)) return null;
      return (
        <BlockShell title={c.title || 'About the evening'}>
          {c.body_html
            ? <div className="prose max-w-none text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: c.body_html }} />
            : <p className="text-neutral-700 leading-relaxed whitespace-pre-line">{c.body}</p>}
        </BlockShell>
      );

    case 'date_time': {
      const start = event.start_datetime || event.start_date;
      const end = event.end_datetime || event.end_date;
      if (!start && isEmpty(c.date_label)) return null;
      const _tz = safeEventTz(event.timezone);
      const _tzOpt = _tz ? { timeZone: _tz } : {};
      const hasClockTime = (iso) => typeof iso === 'string' && /T\d{2}:\d{2}/.test(iso);
      const fmtT = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ..._tzOpt });
      const computedTime = hasClockTime(start)
        ? (hasClockTime(end) ? `${fmtT(start)} - ${fmtT(end)}` : fmtT(start))
        : null;
      const _adminLabel = (c.time_label || '').trim();
      const _looksPlaceholder = !_adminLabel
        || /^tbd\b/i.test(_adminLabel)
        || /set on admin/i.test(_adminLabel)
        || /[\u2013\u2014]/.test(_adminLabel);
      // Bug Y2: same multi-day date-range handling as the hero, so the "When"
      // block never truncates the end date when the event crosses midnight
      // in its local timezone.
      const _sameDay = (a, b) => {
        if (!a || !b) return true;
        try {
          const dayFmt = new Intl.DateTimeFormat('en-CA', {
            year: 'numeric', month: '2-digit', day: '2-digit', ..._tzOpt,
          });
          return dayFmt.format(new Date(a)) === dayFmt.format(new Date(b));
        } catch { return true; }
      };
      const _fmtDate = (iso, opts) => new Date(iso).toLocaleDateString('en-US', { ...opts, ..._tzOpt });
      const _derivedDl = start
        ? (end && !_sameDay(start, end)
            ? `${_fmtDate(start, { weekday: 'long', month: 'long', day: 'numeric' })} - ${_fmtDate(end, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`
            : _fmtDate(start, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }))
        : null;
      // Bug Y2: an admin-authored `c.date_label` is respected UNLESS the event
      // actually spans multiple local days AND the stored label lacks any
      // range separator - in which case the stored value is stale and would
      // silently truncate the end date. Fall back to the derived multi-day
      // label in that case.
      const _labelLooksLikeRange = (s) => !!s && /[\u2013\u2014]|\s-\s|\bto\b|&|\bthrough\b|\bthru\b/i.test(s);
      const _stale = c.date_label && end && start && !_sameDay(start, end) && !_labelLooksLikeRange(c.date_label);
      const dl = _stale ? _derivedDl : (c.date_label || _derivedDl);
      const tl = _looksPlaceholder ? computedTime : _adminLabel;
      return (
        <BlockShell title={c.title || 'When'}>
          <div className="grid gap-3 md:grid-cols-2">
            {dl && <InfoRow icon={CalendarDays} label="Date" value={dl} />}
            {tl && <InfoRow icon={Clock} label="Time" value={tl} />}
            {(c.timezone_label || event.timezone) && (
              <InfoRow icon={Clock} label="Timezone" value={c.timezone_label || event.timezone} />
            )}
          </div>
        </BlockShell>
      );
    }

    case 'venue': {
      const venueName = c.venue_name || event.venue_name;
      const line = c.address_line || event.address_line;
      const city = c.city || event.city;
      const state = c.state || event.state_region || event.state;
      if (isEmpty(venueName) && isEmpty(line) && isEmpty(city)) return null;
      return (
        <BlockShell title={c.title || 'Where'}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary-50 border border-secondary-200 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-5 w-5 text-secondary-700" />
            </div>
            <div>
              {venueName && <div className="font-display text-xl font-semibold text-primary-900">{venueName}</div>}
              {line && <div className="text-neutral-700">{line}</div>}
              <div className="text-neutral-600">{[city, state, c.country || event.country].filter(Boolean).join(', ')}</div>
            </div>
          </div>
        </BlockShell>
      );
    }

    case 'schedule':
      if (isEmpty(c.items)) return null;
      return (
        <BlockShell title={c.title || 'Flow of the evening'}>
          <ol className="space-y-3">
            {c.items.map((it, i) => (
              <li key={i} className="flex gap-4">
                <div className="text-sm font-semibold text-secondary-700 w-20 flex-shrink-0">{it.time || ''}</div>
                <div>
                  <div className="font-semibold text-primary-900">{it.title}</div>
                  {it.description && <div className="text-sm text-neutral-600">{it.description}</div>}
                </div>
              </li>
            ))}
          </ol>
        </BlockShell>
      );

    case 'what_to_expect':
      if (isEmpty(c.items)) return null;
      return (
        <BlockShell title={c.title || 'What to expect'}>
          <div className="flex flex-wrap gap-2">
            {c.items.map((it, i) => (
              <span key={i} className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-secondary-100 to-secondary-50 border border-secondary-200 text-primary-800 shadow-sm">
                {typeof it === 'string' ? it : it.label}
              </span>
            ))}
          </div>
        </BlockShell>
      );

    case 'food':
      if (isEmpty(c.body) && isEmpty(c.body_html)) return null;
      return (
        <BlockShell title={c.title || 'Food & drink'}>
          {c.body_html
            ? <div className="prose max-w-none text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: c.body_html }} />
            : <p className="text-neutral-700 leading-relaxed whitespace-pre-line">{c.body}</p>}
        </BlockShell>
      );

    case 'dress_code':
      if (isEmpty(c.body) && isEmpty(c.body_html)) return null;
      return (
        <BlockShell title={c.title || 'Dress code'}>
          {c.body_html
            ? <div className="prose max-w-none text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: c.body_html }} />
            : <p className="text-neutral-700 leading-relaxed">{c.body}</p>}
        </BlockShell>
      );

    case 'gallery':
      if (isEmpty(c.media_urls)) return null;
      return (
        <BlockShell title={c.title || 'Gallery'}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {c.media_urls.map((url, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden bg-neutral-100">
                <img src={url} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </BlockShell>
      );

    case 'faq':
      if (isEmpty(c.items)) return null;
      return (
        <BlockShell title={c.title || 'FAQ'}>
          <div className="space-y-3">
            {c.items.map((it, i) => (
              <FaqItem key={i} q={it.q || it.question} a={it.a || it.answer} />
            ))}
          </div>
        </BlockShell>
      );

    case 'policies': {
      const p = c.policies || {};
      const rows = [
        ['Dress code',       p.dress_code || event.dress_code],
        ['Parking',          p.parking || event.parking_instructions],
        ['Food',             p.food || event.food_details],
        ['Dietary',          p.dietary || event.dietary_notes],
        ['Hookah / smoking', p.smoking || event.hookah_smoking_info],
        ['Accessibility',    p.accessibility || event.accessibility_info],
        ['Minimum age',      (p.minimum_age ?? event.minimum_age) ? `${p.minimum_age ?? event.minimum_age}+` : null],
        ['ID required',      p.id_required || event.required_identification],
        ['Refund',           p.refund || event.refund_policy],
        ['Cancellation',     p.cancellation || event.cancellation_policy],
        ['Ticket transfer',  p.ticket_transfer || event.ticket_transfer_policy],
        ['Event highlights',  p.photography || event.photography_policy],
        ['Contact',          p.contact_email || event.event_contact_email],
      ].filter(([, v]) => !isEmpty(v));
      const hasBody = !isEmpty(c.body_html) || !isEmpty(c.body);
      if (rows.length === 0 && !hasBody) return null;
      return (
        <BlockShell title={c.title || 'The details'}>
          {hasBody && (
            <div className="prose max-w-none text-neutral-700 mb-4"
                 dangerouslySetInnerHTML={{ __html: c.body_html || c.body }} />
          )}
          {rows.length > 0 && (
            <dl className="grid gap-3 sm:grid-cols-2">
              {rows.map(([k, v]) => (
                <div key={k} className="border border-neutral-100 rounded-xl p-4">
                  <dt className="text-xs uppercase tracking-widest font-semibold text-secondary-700 mb-1">{k}</dt>
                  <dd className="text-sm text-neutral-700 leading-relaxed">{v}</dd>
                </div>
              ))}
            </dl>
          )}
        </BlockShell>
      );
    }

    case 'hosts': {
      // Admin persists rows as content.items[]; also accept content.hosts for backwards compat and event.hosts as legacy schema fallback.
      const hosts = (c.items && c.items.length) ? c.items
                   : (c.hosts && c.hosts.length) ? c.hosts
                   : (event.hosts || []);
      if (isEmpty(hosts)) return null;
      return (
        <BlockShell title={c.title || 'Your hosts'}>
          <div className="grid gap-4 sm:grid-cols-2">
            {hosts.map((h, i) => {
              // Bundle 48.4 - admin editor writes to `image_url` (see
              // VisualBlockEditor's HostPhotoUploader); older seed docs
              // used `photo_url`. Read both so uploads render publicly.
              const hostImg = h.photo_url || h.image_url;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-full bg-neutral-100 overflow-hidden flex-shrink-0">
                    {hostImg && <img src={hostImg} alt={h.name} className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <div className="font-semibold text-primary-900">{h.name}</div>
                    {h.role && <div className="text-xs text-secondary-700 font-semibold uppercase tracking-widest">{h.role}</div>}
                    {h.bio && <div className="text-sm text-neutral-600 mt-1">{h.bio}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </BlockShell>
      );
    }

    case 'sponsors': {
      const sponsors = (c.items && c.items.length) ? c.items
                       : (c.sponsors && c.sponsors.length) ? c.sponsors
                       : (event.sponsors || []);
      if (isEmpty(sponsors)) return null;
      return (
        <BlockShell title={c.title || 'Presented with'}>
          <div className="flex flex-wrap gap-6 items-center">
            {sponsors.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                {s.logo_url && <img src={s.logo_url} alt={s.name} className="h-10 w-auto" />}
                <div>
                  <div className="text-sm font-semibold text-primary-900">{s.name}</div>
                  {s.tier && <div className="text-xs text-neutral-500">{s.tier}</div>}
                </div>
              </div>
            ))}
          </div>
        </BlockShell>
      );
    }

    case 'custom_rich_text':
      if (isEmpty(c.body_markdown) && isEmpty(c.body_html) && isEmpty(c.body)) return null;
      return (
        <BlockShell title={c.title}>
          {(c.body_html || c.body_markdown)
            ? <div className="prose max-w-none text-neutral-700" dangerouslySetInnerHTML={{ __html: c.body_html || c.body_markdown }} />
            : <div className="prose max-w-none text-neutral-700 whitespace-pre-line">{c.body}</div>}
        </BlockShell>
      );

    case 'rsvp_cta':
      // rendered on the right sidebar; skip inline duplicate
      return null;

    default:
      return null;
  }
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-primary-700" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-widest font-semibold text-secondary-700">{label}</div>
      <div className="text-primary-900 font-medium">{value}</div>
    </div>
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-neutral-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between text-left p-4 hover:bg-neutral-50 transition-colors"
      >
        <span className="font-semibold text-primary-900">{q}</span>
        {open ? <ChevronUp className="h-4 w-4 text-neutral-500" /> : <ChevronDown className="h-4 w-4 text-neutral-500" />}
      </button>
      {open && <div className="px-4 pb-4 text-sm text-neutral-700 leading-relaxed">{a}</div>}
    </div>
  );
};

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url) {
  if (!url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export default EventDetailPage;
